import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import makeStyles from "@material-ui/core/styles/makeStyles";
import ConfirmButton, {
  ConfirmButtonTransitionState
} from "@saleor/components/ConfirmButton";
import Hr from "@saleor/components/Hr";
import RadioGroupField, {
  RadioGroupFieldChoice
} from "@saleor/components/RadioGroupField";
import { ExportErrorFragment } from "@saleor/fragments/types/ExportErrorFragment";
import useForm from "@saleor/hooks/useForm";
import useModalDialogOpen from "@saleor/hooks/useModalDialogOpen";
import { DialogProps } from "@saleor/types";
import {
  ExportSubscriptionScope,
  ExportSubscriptionsInput,
  FileTypesEnum
} from "@saleor/types/globalTypes";
import { getFormErrors } from "@saleor/utils/errors";
import getExportErrorMessage from "@saleor/utils/errors/export";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    hr: {
      marginBottom: theme.spacing(3),
      marginTop: theme.spacing(3)
    }
  }),
  {
    name: "SubscriberExportDialog"
  }
);

const initialForm: ExportSubscriptionsInput = {
  fileType: FileTypesEnum.CSV,
  scope: ExportSubscriptionScope.ALL
};

export type SubscriberQuantity = Record<"all" | "filter", number>;
export interface SubscriberExportDialogProps extends DialogProps {
  confirmButtonState: ConfirmButtonTransitionState;
  errors: ExportErrorFragment[];
  subscriberQuantity: SubscriberQuantity;
  selectedSubscribers: number;
  onSubmit: (data: ExportSubscriptionsInput) => void;
}

const formFields: Array<keyof ExportSubscriptionsInput> = ["fileType", "scope"];

const SubscriberExportDialog: React.FC<SubscriberExportDialogProps> = ({
  confirmButtonState,
  errors,
  onClose,
  onSubmit,
  open,
  subscriberQuantity,
  selectedSubscribers
}) => {
  const classes = useStyles({});
  const intl = useIntl();

  const formErrors = getFormErrors(formFields, errors);

  const { change, data, reset, submit } = useForm(initialForm, onSubmit);

  useModalDialogOpen(open, {
    onClose: () => {
      reset();
    }
  });

  const subscribersToExportChoices: Array<RadioGroupFieldChoice<
    ExportSubscriptionScope
  >> = [
    {
      label: intl.formatMessage(
        {
          defaultMessage: "All subscribers ({number})",
          description: "export all subscribers to csv file"
        },
        {
          number: subscriberQuantity.all || "..."
        }
      ),
      value: ExportSubscriptionScope.ALL
    },
    {
      disabled: selectedSubscribers === 0,
      label: intl.formatMessage(
        {
          defaultMessage: "Selected subscribers ({number})",
          description: "export selected subscribers to csv file"
        },
        {
          number: selectedSubscribers
        }
      ),
      value: ExportSubscriptionScope.IDS
    },
    {
      label: intl.formatMessage(
        {
          defaultMessage: "Current search ({number})",
          description: "export filtered subscribers to csv file"
        },
        {
          number: subscriberQuantity.filter || "..."
        }
      ),
      value: ExportSubscriptionScope.FILTER
    }
  ];

  const subscriberExportTypeChoices: Array<RadioGroupFieldChoice<
    FileTypesEnum
  >> = [
    {
      label: intl.formatMessage({
        defaultMessage: "Spreadsheet for Excel, Numbers etc.",
        description: "export subscribers as spreadsheet"
      }),
      value: FileTypesEnum.XLSX
    },
    {
      label: intl.formatMessage({
        defaultMessage: "Plain CSV file",
        description: "export subscribers as csv file"
      }),
      value: FileTypesEnum.CSV
    }
  ];

  return (
    <Dialog onClose={onClose} open={open} maxWidth="sm" fullWidth>
      <>
        <DialogTitle>
          <FormattedMessage
            defaultMessage="Export Information"
            description="export subscribers to csv file, dialog header"
          />
        </DialogTitle>
        <DialogContent>
          <RadioGroupField
            choices={subscribersToExportChoices}
            error={!!formErrors.scope}
            hint={getExportErrorMessage(formErrors.scope, intl)}
            label={intl.formatMessage({
              defaultMessage: "Export information for:",
              description: "export subscribers to csv file, choice field label"
            })}
            name={"scope" as keyof ExportSubscriptionsInput}
            onChange={change}
            value={data.scope}
          />
          <Hr className={classes.hr} />
          <RadioGroupField
            choices={subscriberExportTypeChoices}
            error={!!formErrors.fileType}
            hint={getExportErrorMessage(formErrors.fileType, intl)}
            label={intl.formatMessage({
              defaultMessage: "Export as:",
              description: "export subscribers as csv or spreadsheet file"
            })}
            name={"fileType" as keyof ExportSubscriptionsInput}
            onChange={change}
            value={data.fileType}
          />
        </DialogContent>
        <DialogActions>
          <ConfirmButton
            transitionState={confirmButtonState}
            variant="contained"
            type="submit"
            data-test="submit"
            onClick={submit}
          >
            <FormattedMessage
              defaultMessage="export subscribers"
              description="export subscribers to csv file, button"
            />
          </ConfirmButton>
        </DialogActions>
      </>
    </Dialog>
  );
};

SubscriberExportDialog.displayName = "SubscriberExportDialog";
export default SubscriberExportDialog;
